import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import Icon from '../Icon';
import WorklistColumns, {
  DEFAULT_WORKLIST_COLUMNS,
  MANDATORY_WORKLIST_COLUMNS,
  WORKLIST_COLUMNS_KEY
} from './WorklistColumns';
import HotkeysPreferences from '../HotkeysPreferences';
import Select from '../Select';
import './UserPreferences.css';


const UserPreferences = ({
  availableLanguages,
  defaultLanguage,
  currentLanguage,
  disabled,
  hotkeyDefinitions,
  hotkeyDefaults,
  onCancel,
  onSubmit,
  onReset,
  hotkeysModule,
  isWorklist = false,
  allColumns = [],
}) => {
  const { t } = useTranslation('UserPreferencesModal');

  const defaultColumnNames = useMemo(() => {
    return (allColumns || []).map(col => col.name);
  }, [allColumns]);

  const [state, setState] = useState(() => {
    let initialVisibleColumns = DEFAULT_WORKLIST_COLUMNS;
    if (isWorklist) {
      try {
        const stored = localStorage.getItem(WORKLIST_COLUMNS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            initialVisibleColumns = Array.from(new Set([...MANDATORY_WORKLIST_COLUMNS, ...parsed]));
          }
        }
      } catch (err) {
        console.error('Error reading worklist_columns from localStorage', err);
      }
    }

    return {
      isDisabled: disabled,
      hotkeyErrors: {},
      hotkeyDefinitions,
      language: currentLanguage,
      visibleColumns: initialVisibleColumns,
    };
  });

  const onSubmitHandler = () => {
    if (isWorklist && state.visibleColumns) {
      try {
        const toSave = Array.from(new Set([...MANDATORY_WORKLIST_COLUMNS, ...state.visibleColumns]));
        localStorage.setItem(WORKLIST_COLUMNS_KEY, JSON.stringify(toSave));
      } catch (err) {
        console.error('Error saving worklist_columns to localStorage', err);
      }
    }
    onSubmit(state);
    toast.success(t('Preferences updated'));
    setTimeout(() => window.location.reload(), 1500);
  };

  const onResetHandler = () => {
    if (isWorklist) {
      try {
        localStorage.removeItem(WORKLIST_COLUMNS_KEY);
      } catch (err) {
        console.error('Error clearing worklist_columns from localStorage', err);
      }
    }
    setState(prevState => ({
      ...prevState,
      language: defaultLanguage,
      hotkeyDefinitions: hotkeyDefaults,
      hotkeyErrors: {},
      isDisabled: disabled,
      visibleColumns: DEFAULT_WORKLIST_COLUMNS,
    }));
    onReset();
  };

  const onCancelHandler = () => {
    setState(prevState => ({
      ...prevState,
      hotkeyDefinitions,
    }));
    onCancel();
  };

  const onLanguageChangeHandler = value => {
    setState(state => ({ ...state, language: value }));
  };

  const onHotkeysChangeHandler = (id, definition, errors) => {
    setState(state => ({
      ...state,
      isDisabled: Object.values(errors).every(e => e !== undefined),
      hotkeyErrors: errors,
      hotkeyDefinitions: {
        ...state.hotkeyDefinitions,
        [id]: definition,
      },
    }));
  };

  const handleToggleColumn = columnName => {
    if (MANDATORY_WORKLIST_COLUMNS.includes(columnName)) {
      return;
    }
    setState(prevState => {
      const current = prevState.visibleColumns || [];
      let updated;
      if (current.includes(columnName)) {
        if (current.length <= 1) {
          toast.error(t('At least one column must be visible'));
          return prevState;
        }
        updated = current.filter(name => name !== columnName);
      } else {
        const allNames = (allColumns || []).map(c => c.name);
        updated = allNames.filter(name => name === columnName || current.includes(name));
      }
      return {
        ...prevState,
        visibleColumns: Array.from(new Set([...MANDATORY_WORKLIST_COLUMNS, ...updated])),
      };
    });
  };

  const handleSelectAllColumns = () => {
    setState(prevState => ({
      ...prevState,
      visibleColumns: defaultColumnNames,
    }));
  };

  const Section = ({ title, children }) => (
    <>
      {title && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-1 h-5 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(180deg, #3b82f6, #6366f1)' }}
          />
          <span
            className="text-sm font-bold uppercase tracking-widest"
            style={{ color: '#818cf8' }}
          >
            {title}
          </span>
          <div
            className="flex-1 h-px"
            style={{ background: 'linear-gradient(90deg, rgba(99,102,241,0.3), transparent)' }}
          />
        </div>
      )}
      <div className="mb-8">{children}</div>
    </>
  );

  return (
    <div className="flex flex-col">
      <div>
        <Section title={t('General')}>
          <div className="flex w-full flex-row items-center gap-4">
            <label
              className="text-xs font-semibold uppercase tracking-wider flex-shrink-0"
              style={{ color: '#64748b' }}
            >
              {t('Language')}
            </label>
            <div className="flex-1 max-w-[200px]">
              <Select
                isClearable={false}
                onChange={onLanguageChangeHandler}
                options={availableLanguages}
                value={state.language}
              />
            </div>
          </div>
        </Section>

        {isWorklist && allColumns && allColumns.length > 0 && (
          <Section title={t('Worklist Columns')}>
            <WorklistColumns
              state={state}
              defaultColumnNames={defaultColumnNames}
              handleToggleColumn={handleToggleColumn}
              handleSelectAllColumns={handleSelectAllColumns}
              allColumns={allColumns}
            />
          </Section>
        )}

        {!isWorklist && (
          <Section title={hotkeyDefinitions ? t('Hotkeys') : ''}>
            <HotkeysPreferences
              disabled={disabled}
              hotkeyDefinitions={state.hotkeyDefinitions}
              onChange={onHotkeysChangeHandler}
              errors={state.hotkeyErrors}
              hotkeysModule={hotkeysModule}
            />
          </Section>
        )}

      </div>

      <div style={{ background: 'rgba(11, 15, 25, 0.98)' }}>
        <div
          className="mb-4"
          style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.3), transparent)' }}
        />

        <div className="flex flex-col sm:flex-row sm:justify-between items-stretch sm:items-center gap-3">
          <button
            onClick={onResetHandler}
            disabled={disabled}
            className="user-preferences-reset-btn flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          >
            <Icon name="refresh" className="w-3.5 h-3.5 flex-shrink-0" />
            {t('Reset to defaults')}
          </button>

          <div className="flex flex-row gap-2">
            <button
              onClick={onCancelHandler}
              className="user-preferences-cancel-btn flex-1 sm:flex-none px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            >
              {t('Cancel')}
            </button>

            <button
              disabled={state.isDisabled}
              onClick={onSubmitHandler}
              className="user-preferences-save-btn flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all"
            >
              <Icon className="w-3.5 h-3.5" name="icon-save" />
              {t('Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const noop = () => {};

UserPreferences.propTypes = {
  disabled: PropTypes.bool,
  hotkeyDefaults: PropTypes.object,
  hotkeyDefinitions: PropTypes.object,
  isWorklist: PropTypes.bool,
  allColumns: PropTypes.array,
  languageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })
  ),
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  hotkeysModule: PropTypes.shape({
    initialize: PropTypes.func,
    pause: PropTypes.func,
    unpause: PropTypes.func,
    startRecording: PropTypes.func,
    record: PropTypes.func,
  }),
};

UserPreferences.defaultProps = {
  languageOptions: [
    { value: 'ONE', label: 'ONE' },
    { value: 'TWO', label: 'TWO' },
  ],
  onCancel: noop,
  onSubmit: noop,
  onReset: noop,
  disabled: false,
  isWorklist: false,
  allColumns: [],
};

export default UserPreferences;
